import { ref, onBeforeUnmount } from 'vue';

export function useColumnResize({ minWidth, maxWidth, storageKey, edge = 'right', isRTL = false }) {
  const clamp = w => Math.round(Math.min(maxWidth, Math.max(minWidth, w)));
  const deltaFor = (startX, clientX) => {
    const base = edge === 'left' ? startX - clientX : clientX - startX;
    // RTL mirrors the layout, so the physical drag-to-grow direction flips.
    return isRTL ? -base : base;
  };

  const readStored = () => {
    try {
      const raw = window.localStorage.getItem(storageKey);
      const value = Number(raw);
      if (!raw || Number.isNaN(value)) return null;
      return clamp(value);
    } catch {
      return null;
    }
  };

  const width = ref(readStored());

  let start = null;
  let pending = null;
  let raf = null;

  const onResizeMove = event => {
    if (!start) return;
    pending = clamp(start.width + deltaFor(start.x, event.clientX));
    if (raf) return;
    raf = window.requestAnimationFrame(() => {
      raf = null;
      if (pending != null) width.value = pending;
    });
  };

  const onResizeEnd = () => {
    if (!start) return;
    start = null;
    if (raf) { window.cancelAnimationFrame(raf); raf = null; }
    if (pending != null) { width.value = pending; pending = null; }
    window.removeEventListener('pointermove', onResizeMove);
    window.removeEventListener('pointerup', onResizeEnd);
    window.removeEventListener('pointercancel', onResizeEnd);
    document.body.style.cursor = '';
    document.body.style.userSelect = '';
    try {
      if (width.value != null) window.localStorage.setItem(storageKey, String(width.value));
    } catch { /* localStorage unavailable — width stays session-only */ }
  };

  const onResizeStart = (event, currentWidthPx) => {
    start = { x: event.clientX, width: currentWidthPx };
    window.addEventListener('pointermove', onResizeMove);
    window.addEventListener('pointerup', onResizeEnd);
    window.addEventListener('pointercancel', onResizeEnd);
    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';
    event.preventDefault();
  };

  const cleanup = () => onResizeEnd();
  onBeforeUnmount(cleanup);

  return { width, onResizeStart, cleanup };
}
